import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Plus, Rocket, Trash2, Loader2, Save, Layout } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import RequireAdmin from "@/components/admin/RequireAdmin";
import PlatformToggle from "@/components/admin/PlatformToggle";
import ImageUploadField from "@/components/admin/ImageUploadField";
import { useAdminI18n } from "@/lib/adminI18n";

const ANN_TYPES = [
  { v: "info", label: "Info" },
  { v: "warning", label: "Warning" },
  { v: "promo", label: "Promo" },
];

const DEFAULT_PLATFORMS = ["web", "mobile"];

function blankHero()    { return { title: {}, subtitle: {}, platforms: DEFAULT_PLATFORMS }; }
function blankBanner()  { return { image_url: "", link_url: "", title: {}, active: true, sort_order: 0, platforms: DEFAULT_PLATFORMS }; }
function blankStep()    { return { icon: "ShoppingBag", title: {}, description: {}, platforms: DEFAULT_PLATFORMS }; }
function blankAnn()     { return { text: {}, type: "info", active: true, platforms: DEFAULT_PLATFORMS }; }

function AdminContentPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroes, setHeroes] = useState([]);
  const [banners, setBanners] = useState([]);
  const [steps, setSteps] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [surface, setSurface] = useState("all");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [feedControlsOpen, setFeedControlsOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/admin/content");
        const data = await res.json();
        const editable = data.draft || data;
        setHeroes(Array.isArray(editable.hero) ? editable.hero : []);
        setBanners(Array.isArray(editable.banners) ? editable.banners : []);
        setSteps(Array.isArray(editable.how_it_works) ? editable.how_it_works : []);
        setAnnouncements(Array.isArray(editable.announcements) ? editable.announcements : []);
        setWorkflow(data.workflow || null);
      } catch (e) {
        toast.error(t("content.failedToLoad"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const buildPayload = () => ({
    hero: heroes,
    banners: banners.map((b, i) => ({ ...b, sort_order: Number(b.sort_order ?? i) })),
    how_it_works: steps,
    announcements,
  });

  const applyContentResponse = (data) => {
    const editable = data.draft || data;
    setHeroes(Array.isArray(editable.hero) ? editable.hero : []);
    setBanners(Array.isArray(editable.banners) ? editable.banners : []);
    setSteps(Array.isArray(editable.how_it_works) ? editable.how_it_works : []);
    setAnnouncements(Array.isArray(editable.announcements) ? editable.announcements : []);
    setWorkflow(data.workflow || null);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/admin/content/draft", {
        method: "PUT",
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || t("content.saveFailed"));
      applyContentResponse(data);
      toast.success(t("content.draftSaved", "Draft saved"));
    } catch (e) {
      toast.error(e?.message || t("content.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/admin/content/publish", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || t("content.publishFailed", "Publish failed"));
      applyContentResponse(data);
      toast.success(t("content.published", "Content published"));
    } catch (e) {
      toast.error(e?.message || t("content.publishFailed", "Publish failed"));
    } finally {
      setSaving(false);
    }
  };

  const rowsForSurface = (items) =>
    items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => surface === "all" || (item.platforms || DEFAULT_PLATFORMS).includes(surface));

  const previewRows = [
    ...heroes.map((item) => ({ type: t("content.hero", "Hero"), title: item.title?.en || item.title?.ar || item.id || t("content.untitled", "Untitled"), item })),
    ...banners.map((item) => ({ type: t("content.banner", "Banner"), title: item.title?.en || item.title?.ar || item.id || t("content.untitled", "Untitled"), item })),
    ...steps.map((item) => ({ type: t("content.step", "Step"), title: item.title?.en || item.title?.ar || item.id || t("content.untitled", "Untitled"), item })),
    ...announcements.map((item) => ({ type: t("content.announcement", "Announcement"), title: item.text?.en || item.text?.ar || item.id || t("content.untitled", "Untitled"), item })),
  ].filter(({ item }) => surface === "all" || (item.platforms || DEFAULT_PLATFORMS).includes(surface));

  const addRow = (setter, blank) => setter((arr) => [...arr, blank]);
  const patchRow = (setter, idx, patch) =>
    setter((arr) => arr.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeRow = (setter, idx) =>
    setter((arr) => arr.filter((_, i) => i !== idx));
  const setI18n = (setter, idx, field, lang, val) =>
    setter((arr) =>
      arr.map((x, i) => (i === idx ? { ...x, [field]: { ...(x[field] || {}), [lang]: val } } : x)),
    );
  const hasFeedRows = heroes.length > 0 || banners.length > 0 || steps.length > 0 || announcements.length > 0;
  const showFeedControls = hasFeedRows || feedControlsOpen;

  return (
    <AdminShell
      title={t("content.title")}
      description={t("content.description")}
    >
      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#F37021]" /></div>
      ) : (
        <div className="space-y-8" data-testid="content-admin">
          <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/35">{t("content.workflow", "Draft / Preview / Publish")}</div>
                <div className="mt-2 text-lg font-semibold text-white">
                  {t("content.status", "Status")}: {t(`content.statuses.${workflow?.status || "published"}`, workflow?.status || "published")}
                </div>
                <div className="mt-1 text-xs text-white/45">
                  {t("content.lastSaved", "Last saved")}: {workflow?.last_saved_at?.slice?.(0, 19) || t("content.notSaved", "not saved")} - {t("content.lastPublished", "Last published")}: {workflow?.last_published_at?.slice?.(0, 19) || t("content.notPublished", "not published")}
                </div>
                {!workflow?.app_admin_connected && (
                  <div className="mt-2 text-sm text-amber-200">
                    {t("content.appNotConnected", "App content delivery is not connected yet.")}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  ["all", t("content.all", "All")],
                  ["web", t("content.website", "Website")],
                  ["mobile", t("content.app", "App")],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSurface(id)}
                    className={`h-10 rounded-full border px-4 text-sm font-semibold ${surface === id ? "border-[#F37021] bg-[#F37021] text-white" : "border-white/10 bg-black/20 text-white/70"}`}
                  >
                    {label}
                  </button>
                ))}
                <button type="button" onClick={() => setPreviewOpen((v) => !v)} className="inline-flex h-10 items-center gap-2 rounded-full border border-white/10 px-4 text-sm font-semibold text-white/75">
                  <Eye className="h-4 w-4" />
                  {t("content.preview", "Preview")}
                </button>
                <button type="button" onClick={onPublish} disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-full bg-emerald-500 px-4 text-sm font-semibold text-white disabled:opacity-50">
                  <Rocket className="h-4 w-4" />
                  {t("content.publish", "Publish")}
                </button>
              </div>
            </div>
            {previewOpen && (
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {previewRows.length === 0 ? (
                  <Empty label={t("content.noPreviewItems", "No preview items for this surface.")} />
                ) : previewRows.map((row, index) => (
                  <div key={`${row.type}-${index}`} className="rounded-2xl border border-white/10 bg-black/30 p-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/38">{row.type}</div>
                    <div className="mt-2 line-clamp-2 font-semibold text-white">{row.title}</div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(row.item.platforms || DEFAULT_PLATFORMS).map((platform) => (
                        <span key={platform} className="rounded-full border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-white/50">
                          {platform === "web" ? t("content.website", "Website") : t("content.app", "App")}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!showFeedControls && (
            <div className="admin-panel p-5" data-testid="content-feed-collapsed">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold text-white">{t("content.advancedFeedTitle", "Advanced content feed")}</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-white/45">
                    {t("content.advancedFeedBody", "Homepage feed controls are hidden because no live rows are configured. Shipping, warehouse, product, and account settings remain in their dedicated admin sections. Open this only when you need to create website/mobile feed rows.")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFeedControlsOpen(true)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white/75 transition hover:bg-white/10"
                  data-testid="content-show-feed-controls"
                >
                  <Plus className="h-4 w-4" />
                  {t("content.showFeedControls", "Show feed controls")}
                </button>
              </div>
            </div>
          )}

          {showFeedControls && (
            <>
              {/* HERO LIST */}
              <Section
                title={t("content.heroEntries")}
                action={
                  <button
                    onClick={() => addRow(setHeroes, blankHero())}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold"
                    data-testid="add-hero"
                  >
                    <Plus className="w-3.5 h-3.5" /> {t("content.addHero")}
                  </button>
                }
                hint={t("content.heroHint")}
              >
                {rowsForSurface(heroes).length === 0 ? <Empty label={t("content.noHeroEntries")} /> : rowsForSurface(heroes).map(({ item: h, idx }) => (
                  <RowCard key={idx} testid={`hero-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{t("content.heroItem", "Hero entry")} {idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <PlatformToggle
                          value={h.platforms}
                          onChange={(v) => patchRow(setHeroes, idx, { platforms: v })}
                          testid={`hero-platforms-${idx}`}
                        />
                        <button onClick={() => removeRow(setHeroes, idx)} className="text-red-400/80 hover:text-red-400" data-testid={`hero-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {["en", "ar", "fr"].map((lang) => (
                        <Field key={`t-${lang}`} label={`${t("content.titleLabel", "Title")} (${lang.toUpperCase()})`} value={h.title?.[lang] || ""} onChange={(v) => setI18n(setHeroes, idx, "title", lang, v)} />
                      ))}
                      {["en", "ar", "fr"].map((lang) => (
                        <Field key={`s-${lang}`} label={`${t("content.subtitleLabel", "Subtitle")} (${lang.toUpperCase()})`} value={h.subtitle?.[lang] || ""} onChange={(v) => setI18n(setHeroes, idx, "subtitle", lang, v)} />
                      ))}
                    </div>
                  </RowCard>
                ))}
              </Section>

              {/* BANNERS */}
              <Section
                title={t("content.banners")}
                action={
                  <button onClick={() => addRow(setBanners, blankBanner())} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold" data-testid="add-banner">
                    <Plus className="w-3.5 h-3.5" /> {t("content.addBanner")}
                  </button>
                }
              >
                {rowsForSurface(banners).length === 0 ? <Empty label={t("content.noBanners")} /> : rowsForSurface(banners).map(({ item: b, idx }) => (
                  <RowCard key={idx} testid={`banner-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{t("content.bannerItem", "Banner")} {idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <PlatformToggle value={b.platforms} onChange={(v) => patchRow(setBanners, idx, { platforms: v })} testid={`banner-platforms-${idx}`} />
                        <label className="flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={!!b.active} onChange={(e) => patchRow(setBanners, idx, { active: e.target.checked })} /> {t("content.visible", "Visible")}
                        </label>
                        <button onClick={() => removeRow(setBanners, idx)} className="text-red-400/80 hover:text-red-400" data-testid={`banner-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <ImageUploadField label={t("content.image", "Image")} value={b.image_url} onChange={(v) => patchRow(setBanners, idx, { image_url: v })} apiFetch={apiFetch} testid={`banner-image-${idx}`} />
                      <Field label={t("content.linkUrl", "Link URL")}  value={b.link_url}  onChange={(v) => patchRow(setBanners, idx, { link_url: v })} />
                      <Field label={`${t("content.titleLabel", "Title")} (EN)`} value={b.title?.en || ""} onChange={(v) => setI18n(setBanners, idx, "title", "en", v)} />
                    </div>
                  </RowCard>
                ))}
              </Section>

              {/* HOW IT WORKS */}
              <Section
                title={t("content.steps")}
                action={
                  <button onClick={() => addRow(setSteps, blankStep())} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold" data-testid="add-step">
                    <Plus className="w-3.5 h-3.5" /> {t("content.addStep")}
                  </button>
                }
              >
                {rowsForSurface(steps).length === 0 ? <Empty label={t("content.noSteps")} /> : rowsForSurface(steps).map(({ item: s, idx }) => (
                  <RowCard key={idx} testid={`step-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{t("content.stepItem", "Step")} {idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <PlatformToggle value={s.platforms} onChange={(v) => patchRow(setSteps, idx, { platforms: v })} testid={`step-platforms-${idx}`} />
                        <button onClick={() => removeRow(setSteps, idx)} className="text-red-400/80 hover:text-red-400" data-testid={`step-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-4 gap-3">
                      <Field label={t("content.iconName", "Icon name")} value={s.icon} onChange={(v) => patchRow(setSteps, idx, { icon: v })} />
                      {["en", "ar", "fr"].map((lang) => (
                        <Field key={`t-${lang}`} label={`${t("content.titleLabel", "Title")} (${lang.toUpperCase()})`} value={s.title?.[lang] || ""} onChange={(v) => setI18n(setSteps, idx, "title", lang, v)} />
                      ))}
                      {["en", "ar", "fr"].map((lang) => (
                        <Field key={`d-${lang}`} label={`${t("content.descriptionLabel", "Description")} (${lang.toUpperCase()})`} value={s.description?.[lang] || ""} onChange={(v) => setI18n(setSteps, idx, "description", lang, v)} />
                      ))}
                    </div>
                  </RowCard>
                ))}
              </Section>

              {/* ANNOUNCEMENTS */}
              <Section
                title={t("content.announcements")}
                action={
                  <button onClick={() => addRow(setAnnouncements, blankAnn())} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold" data-testid="add-ann">
                    <Plus className="w-3.5 h-3.5" /> {t("content.addAnnouncement")}
                  </button>
                }
              >
                {rowsForSurface(announcements).length === 0 ? <Empty label={t("content.noAnnouncements")} /> : rowsForSurface(announcements).map(({ item: a, idx }) => (
                  <RowCard key={idx} testid={`ann-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{t("content.announcementItem", "Announcement")} {idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <PlatformToggle value={a.platforms} onChange={(v) => patchRow(setAnnouncements, idx, { platforms: v })} testid={`ann-platforms-${idx}`} />
                        <label className="flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={!!a.active} onChange={(e) => patchRow(setAnnouncements, idx, { active: e.target.checked })} /> {t("content.visible", "Visible")}
                        </label>
                        <button onClick={() => removeRow(setAnnouncements, idx)} className="text-red-400/80 hover:text-red-400" data-testid={`ann-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-4 gap-3">
                      {["en", "ar", "fr"].map((lang) => (
                        <Field key={`a-${lang}`} label={`${t("content.textLabel", "Text")} (${lang.toUpperCase()})`} value={a.text?.[lang] || ""} onChange={(v) => setI18n(setAnnouncements, idx, "text", lang, v)} />
                      ))}
                      <label className="block">
                        <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{t("content.type", "Type")}</span>
                        <select
                          value={a.type}
                          onChange={(e) => patchRow(setAnnouncements, idx, { type: e.target.value })}
                          className="mt-1.5 w-full h-10 px-3 rounded-xl bg-black/40 border border-white/10 text-sm"
                        >
                          {ANN_TYPES.map((type) => <option key={type.v} value={type.v}>{type.label}</option>)}
                        </select>
                      </label>
                    </div>
                  </RowCard>
                ))}
              </Section>
            </>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#F37021] hover:bg-[#E06015] text-sm font-bold disabled:opacity-50"
              data-testid="content-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t("content.saveDraft", "Save draft")}
            </button>
            <button
              type="button"
              onClick={onPublish}
              disabled={saving}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-sm font-bold disabled:opacity-50"
              data-testid="content-publish"
            >
              <Rocket className="w-4 h-4" />
              {t("content.publish", "Publish")}
            </button>
          </div>
        </div>
      )}

    </AdminShell>
  );
}

function Section({ title, action, children, hint }) {
  return (
    <div className="admin-panel p-5">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Layout className="w-4 h-4 text-[#F37021]" /> {title}
          </h2>
          {hint && <p className="text-[11px] text-white/40 mt-1">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function RowCard({ children, testid }) {
  return <div className="admin-panel-soft p-3 mb-3" data-testid={testid}>{children}</div>;
}

function Empty({ label }) {
  return <p className="text-sm text-white/40 italic py-2">{label}</p>;
}

function Field({ label, value, onChange, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-10 px-3 rounded-xl bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[#F37021]"
      />
    </label>
  );
}

export default function AdminContentPage() {
  return (
    <RequireAdmin permission="content.manage">
      <AdminContentPageInner />
    </RequireAdmin>
  );
}
